import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
    Input,
    Card,
    DateTimePicker,
    TextArea,
    RadioButton,
    Tabs,
    Dropdown,
    Spinner
} from '../../../../shared/custom-ui';
import { V2Button } from '../../../../shared/custom-ui/v2-components';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import useValidation from '../../../../hooks/formValidation/useValidation';
import XrayForm from '../../../patient-register/components/categories/xray/Xray';
import CTScanForm from '../../../patient-register/components/categories/ct/CtScanForm';
import UltrasoundForm from '../../../patient-register/components/categories/ultrasound/UltrasoundForm';
import MRIForm from '../../../patient-register/components/categories/mri/mriForm';
import NuclearMedicineForm from '../../../patient-register/components/categories/nuclear-medicine/nuclearMedicineForm';
import IrForm from '../../../patient-register/components/ir/IrForm';
import { toast } from 'react-toastify';
import ApiServices from '../../../../shared/ApiServices/ApiServices';
import LabServices from '../../../../shared/ApiServices/LabServices';
import PageHeader from '../../../../shared/components/pageHeader';
import { buildPatientDisplayName } from '../../../../shared/utils/nameUtils';
import { FlaskConical, ArrowLeft, CalendarPlus, Radio, Search, CheckCircle2, X } from 'lucide-react';

interface IAddScheduling {
    modalPatient?: any;
    toggleModal: (value?: any) => any;
    storedPatients?: any;
    refreshAppointments?: () => void;
}

const validationObj = {
    appointmentDate: ['required'],
    note: [],
};

// ─── Section divider — matches app-wide design language ───────────────────────
const SectionLabel: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
    <div className="flex items-center gap-2 mb-3">
        <span
            className="flex items-center justify-center w-6 h-6 rounded-lg flex-shrink-0"
            style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.08))', color: 'var(--v2-primary-500)' }}
        >
            {icon}
        </span>
        <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">{label}</span>
        <div className="flex-1 h-px" style={{ background: 'var(--v2-border-subtle)' }} />
    </div>
);

// ─── Appointment type options ────────────────────────────────────────────────
const APPT_TYPES = [
    {
        id: 'radiology',
        label: 'Radiology',
        desc: 'X-ray, CT, MRI, Ultrasound, IR, Nuclear',
        Icon: Radio,
        color: '#045EB8',
        bg: 'rgba(4,94,184,0.08)',
        border: 'rgba(4,94,184,0.25)',
    },
    {
        id: 'pathology',
        label: 'Pathology',
        desc: 'Lab tests — CBC, LFT, Thyroid, etc.',
        Icon: FlaskConical,
        color: '#059669',
        bg: 'rgba(5,150,105,0.08)',
        border: 'rgba(5,150,105,0.25)',
    },
] as const;

type ApptType = (typeof APPT_TYPES)[number]['id'];

const AddScheduling = ({
    modalPatient = null,
    toggleModal = null,
    storedPatients = null,
    refreshAppointments = undefined,
}: IAddScheduling) => {
    const location = useLocation();
    const navPatient: any = (location as any)?.state?.selectedPatient;
    const initialPatient = modalPatient || navPatient || null;
    const [patient, setPatient] = useState(initialPatient);
    const [patientSearch, setPatientSearch] = useState(initialPatient ? buildPatientDisplayName(initialPatient) : '');
    const [patientError, setPatientError] = useState('');
    const [apiLoading, setApiLoading] = useState(false);
    const [patientLoading, setPatientLoading] = useState(false);
    const [originalAppointmentDateTime, setOriginalAppointmentDateTime] = useState('');
    const [formState, setFormState] = useState({
        appointmentDate: '',
        note: '',
    });
    const [selectedTestType, setSelectedTestType] = useState('');
    const [formDefaultValues, setFormDefaultValues] = useState({});
    const [prefilledForms, setPrefilledForms] = useState<Record<string, boolean>>({});
    const [errors, setErrors] = useState<any>({});
    const [savingStarted, setSavingStarted] = useState<boolean>(false);
    const { validateField } = useValidation();
    const navigate = useNavigate();
    const [buttonText, setButtonText] = useState('Save');

    // ── Appointment type (radiology | pathology) ─────────────────────────────
    const [appointmentType, setAppointmentType] = useState<ApptType>('radiology');

    // ── Lab tests (for pathology mode) ───────────────────────────────────────
    const [allLabTests, setAllLabTests] = useState<any[]>([]);
    const [labTestsLoading, setLabTestsLoading] = useState(false);
    const [labTestSearch, setLabTestSearch] = useState('');
    const [selectedLabTests, setSelectedLabTests] = useState<Array<{ testId: string; testName: string }>>([]);

    const [allPatients, setAllPatients] = useState(storedPatients);
    const [patientDropdownOpen, setPatientDropdown] = useState(false);

    // Tabs and form categories refs
    const xrayRef = useRef(null);
    const ctRef = useRef(null);
    const mriRef = useRef(null);
    const ultrasoundRef = useRef(null);
    const irRef = useRef(null);
    const nuclearRef = useRef(null);
    const initializedRef = useRef(false);

    useEffect(() => {
        if (!storedPatients) getAllPatients();
    }, []);

    const getAllPatients = async () => {
        try {
            setPatientLoading(true);
            const response = await ApiServices.getAllPatients();
            const patients = Array.isArray(response.items) ? response.items : [];
            setAllPatients(patients);
        } catch (error) {
            console.log('error here ---->', error);
            setAllPatients([]);
        } finally {
            setPatientLoading(false);
        }
    };

    // Fetch lab tests when pathology tab is selected
    useEffect(() => {
        if (appointmentType !== 'pathology') return;
        if (allLabTests.length > 0) return; // already loaded
        setLabTestsLoading(true);
        LabServices.getLabTests()
            .then(res => {
                const tests = res?.tests ?? res?.items ?? (Array.isArray(res) ? res : []);
                setAllLabTests(tests);
            })
            .catch(() => setAllLabTests([]))
            .finally(() => setLabTestsLoading(false));
    }, [appointmentType]);

    // Detect if pre-existing appointment is pathology
    useEffect(() => {
        if (modalPatient?.AppointmentType) {
            const t = String(modalPatient.AppointmentType).toLowerCase();
            if (t === 'pathology') setAppointmentType('pathology');
            else setAppointmentType('radiology');
        }
        if (modalPatient?.Tests && Array.isArray(modalPatient.Tests)) {
            setSelectedLabTests(modalPatient.Tests);
        }
    }, [modalPatient]);

    const normalizeCheckboxValues = (data = {}) => {
        return Object.fromEntries(
            Object.entries(data).map(([key, value]) => [key, String(value)])
        );
    };

    const [activeTab, setActiveTab] = useState<null | string>(null);
    const [mainTabs, setMainTabs] = useState<any[]>([]);

    const tabs = [
        { id: 'x-ray', label: 'X-ray', key: 'X-RAY' },
        { id: 'ct-scan', label: 'CT Scan', key: 'CT' },
        { id: 'nuclear-medicine', label: 'Nuclear Medicine', key: 'NUCLEAR-MEDICINE' },
        { id: 'ultrasound', label: 'Ultrasound (U/S)', key: 'ULTRASOUND' },
        { id: 'mri', label: 'MRI', key: 'MRI' },
        { id: 'ir', label: 'Interventional Radiology (IR)', key: 'IR' },
    ];

    useEffect(() => {
        if (setApiLoading) setApiLoading(apiLoading);

        if (!initializedRef.current && modalPatient) {
            const rawDateTime = modalPatient.SK || '';
            setOriginalAppointmentDateTime(rawDateTime);
            const displayDateTime = rawDateTime.includes('APPT#')
                ? rawDateTime.split('APPT#')[1]
                : rawDateTime;

            const localDateTime = moment.parseZone(displayDateTime).format('YYYY-MM-DDTHH:mm');
            setFormState({
                appointmentDate: localDateTime,
                note: modalPatient.note ?? modalPatient.Note ?? '',
            });

            initializedRef.current = true;
        }
    }, [apiLoading, modalPatient, setApiLoading]);

    useEffect(() => {
        if (appointmentType === 'radiology' && modalPatient?.TestType) {
            const matchedTab = tabs.find(tab => tab.key === modalPatient.TestType);
            if (matchedTab) {
                setMainTabs([matchedTab]);
                setSelectedTestType(matchedTab.key);
            }
        }
    }, [modalPatient, appointmentType]);

    useEffect(() => {
        if (mainTabs.length === 0) {
            setActiveTab(null);
        } else if (!mainTabs.some(tab => tab.id === activeTab)) {
            setActiveTab(mainTabs[0].id);
        }
    }, [mainTabs]);

    useEffect(() => {
        if (modalPatient && !modalPatient.isFromHistory) {
            setButtonText('Update');
        } else {
            setButtonText('Save');
        }
    }, [modalPatient]);

    useEffect(() => {
        if (modalPatient?.Data) {
            setFormDefaultValues(normalizeCheckboxValues(modalPatient.Data));
        }
    }, [modalPatient]);

    useEffect(() => {
        if (selectedTestType && !prefilledForms[selectedTestType]) {
            setPrefilledForms(prev => ({
                ...prev,
                [selectedTestType]: true
            }));
        }
    }, [selectedTestType]);

    const handleTabChange = (tabId: string) => {
        const selectedTab = tabs.find(tab => tab.id === tabId);
        if (!selectedTab) return;

        const isAlreadyPrefilled = prefilledForms[selectedTab.key];

        setMainTabs([selectedTab]);
        setSelectedTestType(selectedTab.key);
        if (!isAlreadyPrefilled) {
            setFormDefaultValues({});
        } else {
            setFormDefaultValues(normalizeCheckboxValues(modalPatient?.Data || {}));
        }
    };

    const getCategoryRef = (key: string) => {
        switch (key) {
            case 'x-ray': return xrayRef;
            case 'ct-scan': return ctRef;
            case 'ultrasound': return ultrasoundRef;
            case 'mri': return mriRef;
            case 'ir': return irRef;
            case 'nuclear-medicine': return nuclearRef;
            default: return null;
        }
    };

    const renderComponents = (tabId: string) => {
        switch (tabId) {
            case 'x-ray': return <XrayForm ref={xrayRef} defaultValues={formDefaultValues} />;
            case 'ct-scan': return <CTScanForm ref={ctRef} defaultValues={formDefaultValues} />;
            case 'ultrasound': return <UltrasoundForm ref={ultrasoundRef} defaultValues={formDefaultValues} />;
            case 'mri': return <MRIForm ref={mriRef} defaultValues={formDefaultValues} />;
            case 'ir': return <IrForm ref={irRef} defaultValues={formDefaultValues} />;
            case 'nuclear-medicine': return <NuclearMedicineForm ref={nuclearRef} defaultValues={formDefaultValues} />;
            default: return null;
        }
    };

    const handleChange = (key: string, value: string | any) => {
        setFormState(prevState => ({
            ...prevState,
            [key]: value === undefined || value === null ? '' : value,
        }));
    };

    const blurValidation = (key: string, value: any) => {
        if (!savingStarted) return;
        setErrors((prevErrors: any) => ({
            ...prevErrors,
            [key]: singleValidation(key, value),
        }));
    };

    const singleValidation = (key: string, value: any) => {
        return validateField(value, validationObj[key]);
    };

    // ── Lab test toggle (pathology mode) ─────────────────────────────────────
    const toggleLabTest = (test: any) => {
        const testId = test.PK ?? test.testId ?? test.id ?? '';
        const testName = test.testName ?? test.name ?? '';
        if (selectedLabTests.find(t => t.testId === testId)) {
            setSelectedLabTests(prev => prev.filter(t => t.testId !== testId));
        } else {
            setSelectedLabTests(prev => [...prev, { testId, testName }]);
        }
    };

    const filteredLabTests = useMemo(() => {
        if (!labTestSearch.trim()) return allLabTests;
        const q = labTestSearch.toLowerCase();
        return allLabTests.filter(t =>
            (t.testName ?? '').toLowerCase().includes(q) ||
            (t.testCode ?? '').toLowerCase().includes(q)
        );
    }, [allLabTests, labTestSearch]);

    const handleSave = async () => {
        setSavingStarted(true);

        const validationErrors = Object.keys(validationObj).reduce((acc: any, key: string) => {
            const error = singleValidation(key, formState[key]);
            if (error?.value) {
                acc[key] = error.msg;
            }
            return acc;
        }, {});

        patientErrorCheck();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length || !patient?.PK) {
            console.error('Validation failed:', validationErrors, 'Patient:', patient);
            return;
        }

        // Pathology requires at least one test
        if (appointmentType === 'pathology' && selectedLabTests.length === 0) {
            toast.error('Please select at least one lab test for a pathology appointment.');
            return;
        }

        setApiLoading(true);
        try {
            if (buttonText === 'Update') {
                // ── Radiology update payload ─────────────────────────────────
                const payloadData = [
                    ...getCategoriesData(),
                    { note: formState.note },
                ];

                const updatePayload: any = {
                    patientId: patient.PK,
                    oldAppointmentDateTime: originalAppointmentDateTime,
                    newAppointmentDateTime: formState.appointmentDate,
                    note: formState.note,
                    data: payloadData,
                };

                if (appointmentType === 'pathology' && selectedLabTests.length > 0) {
                    updatePayload.tests = selectedLabTests;
                } else if (appointmentType === 'radiology' && selectedTestType) {
                    updatePayload.testType = selectedTestType;
                }

                const res = await ApiServices.modifyAppointment(updatePayload);
                toast.success(res?.message);
            } else {
                // ── New appointment payload ───────────────────────────────────
                let savePayload: any = {
                    patientId: patient.PK,
                    appointmentDateTime: formState.appointmentDate,
                    appointmentType,
                    note: formState.note,
                };

                if (appointmentType === 'pathology') {
                    savePayload.tests = selectedLabTests;
                    savePayload.data = [{ note: formState.note }];
                } else {
                    const payloadData = [
                        ...getCategoriesData(),
                        { note: formState.note },
                    ];
                    savePayload.data = payloadData;
                }

                const res = await ApiServices.saveAppointment(savePayload);
                toast.success(res?.message);
            }

            if (typeof refreshAppointments === 'function') {
                refreshAppointments();
            }

            if (toggleModal) toggleModal(false);
            resetFormValue();

            if (!modalPatient || modalPatient?.isFromHistory) {
                navigate('/scheduling');
            }

        } catch (err: any) {
            const errorMessage =
                err?.response?.data?.message ||
                (buttonText === 'Update'
                    ? 'Error updating appointment!'
                    : 'Error scheduling appointment!');
            toast.error(errorMessage);
            console.error('Error:', err);
        } finally {
            setApiLoading(false);
        }
    };

    const togglePatientDropdown = (value: boolean = false) => {
        setPatientDropdown(value || !patientDropdownOpen);
    };

    const patientInput = () => {
        return (
            <div onClick={() => togglePatientDropdown()}>
                <Input
                    label="Select Patient"
                    value={patientSearch}
                    onChange={(e: any) => {
                        setPatientSearch(e.target.value);
                        if (patient) setPatient(null);
                    }}
                    error={patientError}
                    onClick={() => {
                        if (!savingStarted) return;
                        patientErrorCheck();
                    }}
                    onBlur={() => {
                        if (!savingStarted) return;
                        patientErrorCheck();
                    }}
                />
            </div>
        );
    };

    // ***** getting Categories Data now
    const getCategoriesData = () => {
        const ref = getCategoryRef(mainTabs[0]?.id);
        const formData = ref?.current?.getData?.() || {};
        const stringifiedData = Object.fromEntries(
            Object.entries(formData).map(([key, value]) => [key, String(value)])
        );

        return [{
            testType: selectedTestType,
            data: stringifiedData,
        }];
    };

    const DropdownPatientBody = () => {
        useEffect(() => { }, []);

        if (patientLoading) {
            return (
                <div className="flex justify-center items-center h-[150px]">
                    <Spinner size="md" />
                </div>
            );
        }

        if (filteredPatients.length === 0) {
            return (
                <div className="p-4 text-center text-[15px] text-[var(--v2-text-muted)]">
                    No patients found.
                </div>
            );
        }

        return (
            <ul className="max-h-[350px] w-full overflow-y-auto text-[15px] custom-scrollbar rounded-lg divide-y divide-[var(--v2-border-subtle)] bg-[var(--v2-bg-card)] text-[var(--v2-text-primary)]">
                {filteredPatients?.map((patient: any, index: number) => (
                    <li key={index}>
                        <a
                            onClick={() => {
                                setPatient(patient);
                                setPatientSearch(buildPatientDisplayName(patient));
                                setPatientDropdown(false);
                            }}
                            className="block cursor-pointer px-4 py-2.5 transition duration-150 hover:opacity-80"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex flex-col leading-tight">
                                    <span className="text-[15px] font-semibold text-[var(--v2-text-heading)]">
                                        {buildPatientDisplayName(patient)}
                                    </span>
                                    <span className="text-[14px] mt-0.5 text-[var(--v2-text-muted)]">
                                        MRN: <span className="font-medium">{patient.MRN}</span>
                                    </span>
                                </div>
                            </div>
                        </a>
                    </li>
                ))}
            </ul>
        );
    };

    const filteredPatients = useMemo(() => {
        if (!Array.isArray(allPatients)) return [];
        return allPatients.sort((a: any, b: any) => {
            const aName = buildPatientDisplayName(a).toLowerCase();
            const bName = buildPatientDisplayName(b).toLowerCase();
            const searchLower = patientSearch.toLowerCase();

            const aMatch = aName.includes(searchLower);
            const bMatch = bName.includes(searchLower);

            if (aMatch && !bMatch) return -1;
            if (!aMatch && bMatch) return 1;
            return 0;
        });
    }, [allPatients, patientSearch]);

    const patientErrorCheck = () => {
        if (!patient?.PK) {
            setPatientError('Patient is required');
        } else {
            setPatientError('');
        }
    };

    const resetFormValue = () => {
        setFormState({
            appointmentDate: '',
            note: '',
        });
        setPatient(null);
        setPatientSearch('');
        setPatientError('');
        setMainTabs([]);
        setActiveTab(null);
        setSavingStarted(false);
        setOriginalAppointmentDateTime('');
        setPrefilledForms({});
        setSelectedLabTests([]);
        setLabTestSearch('');
        setAppointmentType('radiology');
    };

    return (
        <>
            {!modalPatient && (
                <section
                    className="relative flex-shrink-0 mb-4 rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-card overflow-hidden"
                    aria-label="Add appointment"
                >
                    <div aria-hidden className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                        <div className="absolute inset-0" style={{ background: 'var(--v2-bg-hero)' }} />
                        <div
                            className="absolute inset-x-0 top-0 h-[3px]"
                            style={{ background: 'linear-gradient(90deg, var(--v2-primary-500) 0%, var(--v2-primary-400) 55%, transparent 100%)' }}
                        />
                    </div>

                    <div className="relative z-10 flex items-center gap-3 px-5 h-[52px]">
                        <V2Button
                            variant="outline"
                            size="small"
                            onClick={() => navigate(-1)}
                            className="!rounded-full !w-8 !h-8 !p-0 !min-h-0 flex-shrink-0 hover:!bg-[var(--v2-bg-hover)]"
                            leftIcon={<ArrowLeft className="w-4 h-4 text-[var(--v2-text-secondary)]" />}
                            title="Go back"
                        />

                        <span
                            className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg"
                            style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.08))', color: 'var(--v2-primary-500)' }}
                        >
                            <CalendarPlus className="w-4 h-4" />
                        </span>

                        <div className="flex items-center gap-2 min-w-0">
                            <h1 className="text-[15px] font-bold tracking-tight text-[var(--v2-text-heading)] whitespace-nowrap m-0 leading-none">
                                {buttonText === 'Update' ? 'Update Appointment' : 'Add Appointment'}
                            </h1>
                            <span className="hidden sm:block w-px h-3.5 flex-shrink-0" style={{ background: 'var(--v2-border-subtle)' }} aria-hidden />
                            <p className="hidden sm:block text-[12px] text-[var(--v2-text-muted)] m-0 truncate">
                                {buttonText === 'Update'
                                    ? 'Edit the appointment details below.'
                                    : 'Fill the form below to schedule an appointment.'}
                            </p>
                        </div>
                    </div>
                </section>
            )}

            <Card variant="default" className="overflow-visible"
                footer={
                    <div className="mt-6 flex justify-end gap-2">
                        <V2Button
                            variant="danger"
                            size="large"
                            onClick={() => {
                                if (toggleModal) {
                                    toggleModal(false);
                                } else {
                                    navigate('/scheduling');
                                }
                            }}
                            disabled={apiLoading}
                        >
                            Cancel
                        </V2Button>

                        <V2Button
                            variant="primary"
                            size="large"
                            onClick={handleSave}
                            loading={apiLoading}
                        >
                            {buttonText}
                        </V2Button>
                    </div>
                }
            >
                <div className={`flex flex-col p-6 ${modalPatient && 'h-full justify-between'}`}>
                    {/* 1) Date & Patient */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="flex flex-col">
                            <DateTimePicker
                                label="Date and Time"
                                value={formState.appointmentDate}
                                onChange={date => handleChange('appointmentDate', date)}
                                pickerType="date-time"
                                maxDate={null}
                                minDate={new Date().toISOString().split('T')[0]}
                                minTime={new Date().toISOString().split('T')[1].slice(0, 5)}
                                error={errors.appointmentDate?.msg}
                                onBlur={() => blurValidation('appointmentDate', formState.appointmentDate)}
                            />
                        </div>

                        <div className="flex flex-col">
                            {!modalPatient ? (
                                <Dropdown
                                    actionComponent={patientInput()}
                                    isOpen={patientDropdownOpen}
                                >
                                    <DropdownPatientBody />
                                </Dropdown>
                            ) : null}
                        </div>
                    </div>

                    {/* 2) Note */}
                    <div className="mt-4">
                        <TextArea
                            label="Note"
                            value={formState.note}
                            onChange={(e: any) => handleChange('note', e.target.value)}
                        />
                    </div>

                    {/* 3) Appointment Type selector — only shown for new appointments */}
                    {!modalPatient && (
                        <div className="mt-5">
                            <div className="mb-2 text-[15px] font-semibold text-[var(--v2-text-primary)]">
                                Appointment Type
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {APPT_TYPES.map(({ id, label, desc, Icon, color, bg, border }) => {
                                    const active = appointmentType === id;
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => setAppointmentType(id)}
                                            className="flex items-center gap-3 rounded-xl border p-3.5 text-left cursor-pointer transition-all"
                                            style={active
                                                ? { background: bg, borderColor: border, boxShadow: `0 0 0 2px ${border}` }
                                                : { background: 'var(--v2-bg-surface)', borderColor: 'var(--v2-border-subtle)' }}
                                        >
                                            <span
                                                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                                style={{ background: active ? bg : 'var(--v2-bg-card)', border: `1.5px solid ${active ? border : 'var(--v2-border-subtle)'}` }}
                                            >
                                                <Icon size={18} style={{ color: active ? color : 'var(--v2-text-muted)' }} />
                                            </span>
                                            <span>
                                                <p className="text-[13px] font-bold m-0" style={{ color: active ? color : 'var(--v2-text-primary)' }}>
                                                    {label}
                                                </p>
                                                <p className="text-[11px] m-0 mt-0.5" style={{ color: 'var(--v2-text-muted)' }}>
                                                    {desc}
                                                </p>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* 4a) RADIOLOGY — Modality selector + form */}
                    {appointmentType === 'radiology' && (
                        <>
                            <div className="mt-4">
                                <div className="mb-2 text-[15px] font-semibold text-[var(--v2-text-primary)]">Select Modality</div>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                                    {tabs.map(tab => (
                                        <RadioButton
                                            key={tab.id}
                                            checked={mainTabs?.some(t => t.id === tab.id)}
                                            onChange={() => handleTabChange(tab.id)}
                                            label={tab.label}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="mt-4">
                                <Tabs
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                    tabs={mainTabs}
                                >
                                    {activeTab && mainTabs.length
                                        ? mainTabs.map((t, index) => (
                                            <div
                                                key={index}
                                                className={`${activeTab === t.id ? '' : 'hidden'}`}
                                            >
                                                {renderComponents(t.id)}
                                            </div>
                                        ))
                                        : null}
                                </Tabs>
                            </div>
                        </>
                    )}

                    {/* 4b) PATHOLOGY — Lab test picker */}
                    {appointmentType === 'pathology' && (
                        <div className="mt-4">
                            <div className="mb-2 text-[15px] font-semibold text-[var(--v2-text-primary)]">
                                Select Lab Tests
                                {selectedLabTests.length > 0 && (
                                    <span
                                        className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-black text-white"
                                        style={{ background: '#059669' }}
                                    >
                                        {selectedLabTests.length} selected
                                    </span>
                                )}
                            </div>

                            {/* Selected tests chips */}
                            {selectedLabTests.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-3">
                                    {selectedLabTests.map(t => (
                                        <span
                                            key={t.testId}
                                            className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-semibold"
                                            style={{ background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' }}
                                        >
                                            {t.testName}
                                            <button
                                                type="button"
                                                onClick={() => setSelectedLabTests(prev => prev.filter(s => s.testId !== t.testId))}
                                                className="w-4 h-4 flex items-center justify-center rounded-full cursor-pointer border-0 bg-transparent hover:bg-[rgba(5,150,105,0.2)]"
                                                style={{ color: '#059669' }}
                                            >
                                                <X size={10} strokeWidth={2.5} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Search bar */}
                            <div className="relative mb-2">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                                    style={{ color: 'var(--v2-text-muted)' }}
                                />
                                <input
                                    type="text"
                                    placeholder="Search by test name or code…"
                                    value={labTestSearch}
                                    onChange={e => setLabTestSearch(e.target.value)}
                                    className="w-full pl-8 pr-3 py-2 rounded-xl text-[12px] border outline-none"
                                    style={{
                                        background: 'var(--v2-bg-surface)',
                                        borderColor: 'var(--v2-border-subtle)',
                                        color: 'var(--v2-text-primary)',
                                    }}
                                />
                            </div>

                            {/* Test list */}
                            <div
                                className="rounded-xl border overflow-hidden overflow-y-auto"
                                style={{
                                    maxHeight: 260,
                                    borderColor: 'var(--v2-border-subtle)',
                                    background: 'var(--v2-bg-surface)',
                                }}
                            >
                                {labTestsLoading ? (
                                    <div className="flex justify-center items-center h-[100px]">
                                        <Spinner size="md" />
                                    </div>
                                ) : filteredLabTests.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-[12px]" style={{ color: 'var(--v2-text-muted)' }}>
                                        {allLabTests.length === 0 ? 'No lab tests found in catalog.' : 'No tests match your search.'}
                                    </div>
                                ) : (
                                    filteredLabTests.map((t: any) => {
                                        const testId = t.PK ?? t.testId ?? t.id ?? '';
                                        const testName = t.testName ?? t.name ?? '';
                                        const price = t.price ?? '';
                                        const category = t.category ?? '';
                                        const isSelected = selectedLabTests.some(s => s.testId === testId);
                                        return (
                                            <button
                                                key={testId}
                                                type="button"
                                                onClick={() => toggleLabTest(t)}
                                                className="w-full flex items-center gap-3 px-4 py-2.5 border-b cursor-pointer text-left bg-transparent transition-colors"
                                                style={{
                                                    borderColor: 'var(--v2-border-subtle)',
                                                    background: isSelected ? 'rgba(5,150,105,0.06)' : undefined,
                                                }}
                                            >
                                                <span
                                                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                                                    style={{
                                                        background: isSelected ? '#059669' : 'var(--v2-bg-card)',
                                                        border: `1.5px solid ${isSelected ? '#059669' : 'var(--v2-border-card)'}`,
                                                    }}
                                                >
                                                    {isSelected && <CheckCircle2 size={12} color="white" strokeWidth={2.5} />}
                                                </span>
                                                <span className="flex-1 min-w-0">
                                                    <p className="text-[12px] font-semibold m-0 truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                                        {testName}
                                                    </p>
                                                    {category && (
                                                        <p className="text-[10px] m-0 mt-0.5" style={{ color: 'var(--v2-text-muted)' }}>
                                                            {category}
                                                        </p>
                                                    )}
                                                </span>
                                                {price && (
                                                    <span className="text-[11px] font-bold flex-shrink-0" style={{ color: '#059669' }}>
                                                        ₹{price}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
};

export default AddScheduling;
